/**
 * DataDensePanel - Maximum Data Density QuantX Display
 *
 * Compact pivot-table style panel showing ALL available data
 * in minimal vertical space. No wasted space, institutional grade.
 *
 * Components:
 * - PriceContextBar: Symbol, price, trend, session, ATR (28px)
 * - EMAPivotTable: Multi-TF EMA matrix (80px)
 * - ADXPivotTable: Multi-TF ADX/ATR matrix (80px)
 * - FibCompactTable: Horizontal Fib levels (90px)
 * - SignalCompactBar: Signal summary (40px)
 * - StrategyDNASection: Bounce/break/fakeout analytics (F3.1/F3.2) (~250px expanded)
 * - PositionSummaryBar: Position status (36px)
 *
 * Total: ~354px base + ~250px DNA section when expanded
 */

import React, { memo } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Tooltip,
    CircularProgress
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

import useQuantXData from '../../hooks/useQuantXData';
import { useSignalRContext } from '../../SignalR/useSignalR';
import PriceContextBar from './tables/PriceContextBar';
import EMAPivotTable from './tables/EMAPivotTable';
import ADXPivotTable from './tables/ADXPivotTable';
import FibCompactTable from './tables/FibCompactTable';
import SignalCompactBar from './tables/SignalCompactBar';
import StrategyDNASection from './tables/StrategyDNASection';
import PositionSummaryBar from './PositionSummaryBar';
import CollapsibleSection from './CollapsibleSection';

/**
 * Default available symbols for trading
 */
const DEFAULT_SYMBOLS = [
    'XAUUSD_ct',
    'BTCUSD_ct',
    'EURUSD_ct',
    'GBPUSD_ct',
    'XAGUSD_ct',
    'ETHUSD_ct'
];

/**
 * DataDensePanel Component
 */
const DataDensePanel = memo(({
    symbol = 'XAUUSD_ct',
    token,
    autoRefresh = true,
    refreshInterval = 30000,
    strategyId = null,
    availableSymbols = DEFAULT_SYMBOLS,
    onSymbolChange
}) => {
    // Get positions from SignalR context
    const { positionsList = [] } = useSignalRContext() || {};

    // Fetch QuantX data (including extended endpoints from Task 1.1-1.4)
    const {
        currentState,
        strategySignal,
        strategyDashboard,
        // Extended data (Task 1.1-1.4)
        trendFilterStats,    // Task 1.1: Detailed EMA bars-above
        adxAnalysis,         // Task 1.2: Detailed ADX/ATR analysis (DEP-2: now includes D1)
        aiContext,           // Task 1.4: Pre-computed AI context
        fibLevels,           // DEP-1: New Fibonacci levels endpoint
        // State flags
        isLoading,
        isRefreshing,
        hasData,
        hasError,
        hasInsufficientData,
        error,
        trendScore,
        overallTrend,
        signal,
        confidence,
        session,
        signalRConnected,
        refresh
    } = useQuantXData({
        symbol,
        token,
        autoRefresh,
        refreshInterval,
        enabled: !!token && !!symbol,
        useSignalR: true
    });

    // Extract ATR data from currentState.volatility
    const volatility = currentState?.volatility || {};
    const atrValue = volatility.h4Atr;
    const atrPercent = currentState?.price > 0 && atrValue
        ? (atrValue / currentState.price * 100)
        : 0;

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                maxHeight: 'calc(100vh - 140px)',
                gap: 0.75,
                overflow: 'hidden'
            }}
        >
            {/* Price Context Bar - Always visible */}
            <PriceContextBar
                symbol={symbol}
                currentPrice={currentState?.price}
                overallTrend={overallTrend}
                trendScore={trendScore}
                session={session}
                atr={atrValue}
                atrPercent={atrPercent}
                signalRConnected={signalRConnected}
                onRefresh={refresh}
                isLoading={isRefreshing}
                availableSymbols={availableSymbols}
                onSymbolChange={onSymbolChange}
            />

            {/* Scrollable Content */}
            <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {/* Loading State */}
                {isLoading && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100px',
                            gap: 1
                        }}
                    >
                        <CircularProgress size={20} sx={{ color: '#ef4444' }} />
                        <Typography sx={{ fontSize: '10px', color: '#9ca3af' }}>
                            Loading...
                        </Typography>
                    </Box>
                )}

                {/* Error State */}
                {hasError && !isLoading && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '80px',
                            gap: 0.5
                        }}
                    >
                        <Typography sx={{ fontSize: '11px', color: '#ef4444', fontWeight: 700 }}>
                            Failed to load data
                        </Typography>
                        <Typography sx={{ fontSize: '9px', color: '#9ca3af' }}>
                            {error?.message || 'Unknown error'}
                        </Typography>
                        <IconButton onClick={refresh} size="small" sx={{ color: '#ef4444', padding: '4px' }}>
                            <RefreshIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                    </Box>
                )}

                {/* Data Display - Only when we have valid QuantX data */}
                {hasData && !isLoading && !hasInsufficientData && (
                    <>
                        {/* EMA TREND MATRIX */}
                        <CollapsibleSection
                            title="EMA Trend Matrix"
                            badge={trendScore ? `${trendScore}/100` : null}
                            defaultExpanded={true}
                        >
                            <EMAPivotTable
                                data={strategyDashboard?.trendAnalysis}
                                currentState={currentState}
                                trendFilterStats={trendFilterStats}  // Task 1.1: Extended trend data
                            />
                        </CollapsibleSection>

                        {/* ADX/ATR MOMENTUM */}
                        <CollapsibleSection
                            title="Momentum & Volatility"
                            badge={adxAnalysis?.h4?.adx ? `ADX ${adxAnalysis.h4.adx.toFixed(1)}` : (currentState?.volatility?.h4Adx ? `ADX ${currentState.volatility.h4Adx.toFixed(1)}` : null)}
                            defaultExpanded={true}
                        >
                            <ADXPivotTable
                                data={strategyDashboard?.adxAnalysis}
                                currentState={currentState}
                                adxAnalysis={adxAnalysis}  // Task 1.2: Extended ADX data
                            />
                        </CollapsibleSection>

                        {/* FIBONACCI LEVELS - DEP-1: Prefer new fib-levels endpoint, fallback to strategyDashboard */}
                        <CollapsibleSection
                            title="Fibonacci Levels"
                            badge={fibLevels?.nearestLevel || strategyDashboard?.fibonacciLevels?.nearestLevel || null}
                            defaultExpanded={true}
                        >
                            <FibCompactTable
                                data={fibLevels || strategyDashboard?.fibonacciLevels}
                                currentPrice={currentState?.price}
                            />
                        </CollapsibleSection>

                        {/* SIGNAL SUMMARY */}
                        <CollapsibleSection
                            title="Trading Signal"
                            badge={signal || null}
                            badgeColor={signal === 'BUY' ? '#22c55e' : signal === 'SELL' ? '#ef4444' : '#fbbf24'}
                            defaultExpanded={true}
                        >
                            <SignalCompactBar
                                data={strategySignal}
                                currentState={currentState}
                            />
                        </CollapsibleSection>

                        {/* STRATEGY DNA (F3.1 & F3.2) */}
                        <StrategyDNASection
                            symbol={symbol}
                            token={token}
                            defaultExpanded={false}
                            refreshInterval={60000}
                        />
                    </>
                )}

                {/* Insufficient Historical Data State */}
                {hasInsufficientData && !isLoading && (
                    <>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '12px',
                                gap: 0.5,
                                backgroundColor: 'rgba(251, 191, 36, 0.1)',
                                borderRadius: '4px',
                                border: '1px solid rgba(251, 191, 36, 0.3)'
                            }}
                        >
                            <Typography sx={{ fontSize: '11px', color: '#fbbf24', fontWeight: 700 }}>
                                QuantX Analytics Unavailable
                            </Typography>
                            <Typography sx={{ fontSize: '9px', color: '#9ca3af', textAlign: 'center', lineHeight: 1.4 }}>
                                {symbol} requires 100+ H4 bars for EMA/ADX/Fib calculations.
                            </Typography>
                            <Typography sx={{ fontSize: '8px', color: '#6b7280', textAlign: 'center' }}>
                                OHLC data accumulating. Try XAUUSD_ct for full analytics.
                            </Typography>
                        </Box>

                        {/* Strategy DNA still works for all symbols */}
                        <StrategyDNASection
                            symbol={symbol}
                            token={token}
                            defaultExpanded={true}
                            refreshInterval={60000}
                        />
                    </>
                )}

                {/* No Data State */}
                {!hasData && !isLoading && !hasError && !hasInsufficientData && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '80px',
                            gap: 0.5
                        }}
                    >
                        <Typography sx={{ fontSize: '10px', color: '#9ca3af' }}>
                            No data for {symbol}
                        </Typography>
                        <Typography sx={{ fontSize: '9px', color: '#6b7280' }}>
                            Check DataFeed Server
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Position Summary Bar - Fixed at bottom */}
            <PositionSummaryBar
                positions={positionsList}
                strategyId={strategyId}
                showDetails={true}
            />
        </Box>
    );
});

DataDensePanel.displayName = 'DataDensePanel';

export default DataDensePanel;
